

import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { classCounts } from '@/config/display-class';
import type { CardRarity } from '@/types';

/**
 * Live cumulative-gate progress for Best Ball entry: "Rare 2/3 · Legendary 0/1".
 * Gates are COUNTS, not floors — Commons are always allowed, and a Legendary
 * card counts toward both requirements (GOAT ⊂ Rare-or-better).
 */

export interface BestBallGates {
  minRare: number;
  minLegendary: number;
}

/** Whether the selected rarities satisfy the cumulative gates. */
export function gatesMet(gates: BestBallGates, rarities: CardRarity[]): boolean {
  const { haveRare, haveLegendary } = classCounts(rarities);
  return haveRare >= gates.minRare && haveLegendary >= gates.minLegendary;
}

function GatePill({
  label,
  have,
  need,
}: {
  label: string;
  have: number;
  need: number;
}) {
  const met = have >= need;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-xs font-bold',
        met
          ? 'bg-verdigris/10 border-verdigris/30 text-verdigris'
          : 'bg-surface-3 border-surface-4 text-warm-gray',
      )}
    >
      {met && <Check className="h-3 w-3" />}
      {label}{' '}
      <span className="font-mono tabular-nums">
        {Math.min(have, need)}/{need}
      </span>
    </span>
  );
}

export function GateMeter({
  gates,
  selectedRarities,
  className,
}: {
  gates: BestBallGates;
  /** Rarities of the currently assigned cards. */
  selectedRarities: CardRarity[];
  className?: string;
}) {
  // No gates configured — nothing to meter.
  if (gates.minRare <= 0 && gates.minLegendary <= 0) return null;

  const { haveRare, haveLegendary } = classCounts(selectedRarities);

  return (
    <div className={cn('flex items-center gap-2 flex-wrap', className)}>
      <span className="text-[10px] uppercase tracking-wider text-tertiary font-bold">
        League gates
      </span>
      {gates.minRare > 0 && (
        <GatePill label="Rare+" have={haveRare} need={gates.minRare} />
      )}
      {gates.minLegendary > 0 && (
        <GatePill label="Legendary" have={haveLegendary} need={gates.minLegendary} />
      )}
    </div>
  );
}
